import React, { useState } from 'react';
import axios from 'axios';
import { BellRing, CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RestockAlertForm = ({ productId }) => {
  const { language } = useLanguage();
  const T = usePageT();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/products/${productId}/restock-alert`, {
        email: email.trim(),
        language,
      });
      setSubscribed(true);
      toast.success(
        r.data.status === 'already_subscribed'
          ? T('Vous êtes déjà inscrit pour ce produit ✓', 'You are already subscribed for this product ✓')
          : T('Alerte enregistrée ! Nous vous préviendrons par email.', 'Alert saved! We will email you.')
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (subscribed) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="restock-alert-confirmed">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-800 font-medium">
          {T('C\'est noté ! Nous vous enverrons un email dès le retour en stock.', 'Done! We will email you as soon as it is back in stock.')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="restock-alert-form">
      <div className="flex items-center gap-2">
        <BellRing className="w-5 h-5 text-amber-600" />
        <p className="font-semibold text-amber-900 text-sm">
          {T('Soyez alerté du retour en stock', 'Get notified when back in stock')}
        </p>
      </div>
      <p className="text-xs text-amber-800/80">
        {T('Laissez votre email et nous vous préviendrons dès que ce produit sera de nouveau disponible.', 'Leave your email and we will notify you as soon as this product is available again.')}
      </p>
      <div className="flex gap-2">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={T('votre@email.com', 'your@email.com')}
          className="bg-white"
          data-testid="restock-alert-email-input"
        />
        <Button type="submit" disabled={submitting} className="btn-primary shrink-0" data-testid="restock-alert-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : T('M\'alerter', 'Notify me')}
        </Button>
      </div>
    </form>
  );
};
